import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Form,
  Input,
  Button,
  Space,
  Typography,
  Steps,
  Tag,
  Alert,
  Collapse,
  Tooltip,
  Switch,
  message,
} from 'antd';
import {
  LoadingOutlined,
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  BulbOutlined,
} from '@ant-design/icons';
import aiReplyService, {
  AIReplyRecommendResponse,
  CommentContext,
  SubredditGuidelinesResponse,
  AdvancedStrategyPreset,
  AdvancedIntent,
  AdvancedAudienceAwareness,
  AdvancedPersona,
  AdvancedDepth,
  AdvancedReplyFormat,
  AdvancedMentionProduct,
  AdvancedCtaType,
  AdvancedToneOverlay,
} from '../../../services/aiReplyService';
import { NestedComment } from '../../../services/replyPostsService';
import { CommentTarget, GenerateParams } from './types';
import KeywordsInput from './KeywordsInput';

const { TextArea } = Input;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Design tokens (Reddit-inspired neutral palette)
// ---------------------------------------------------------------------------

const T = {
  brand: '#FF4500',
  brandHover: '#E03D00',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  border: '#E5E7EB',
  borderStrong: '#D5D7DB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
  textMuted: '#898A8F',
  warning: '#B45309',
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdvancedReplyFormHandle {
  generate: () => void;
}

interface AdvancedReplyFormProps {
  postTitle: string;
  postContent: string;
  postUrl?: string;
  subreddit?: string;
  brandId?: string;
  brandInfo?: string;
  initialKeywords?: string[];
  commentTarget?: CommentTarget;
  guidelines: SubredditGuidelinesResponse | null;
  /** Controlled from outside (provider-owned generation state) */
  loading: boolean;
  currentStage: number;
  onCanGenerateChange: (can: boolean) => void;
  /** Called when the user submits the form; caller owns the async API call */
  onStartGeneration: (params: GenerateParams) => void;
}

// ---------------------------------------------------------------------------
// Option catalogues (mirror backend SET D framework)
// ---------------------------------------------------------------------------

type PillOption<T extends string> = {
  value: T;
  label: string;
  description: string;
};

const STRATEGY_PRESETS: Array<PillOption<AdvancedStrategyPreset>> = [
  { value: 'safe', label: 'Safe', description: 'Educational, low-risk, no selling.' },
  { value: 'high_engagement', label: 'High Engagement', description: 'Hook + invite discussion.' },
  { value: 'conversion_focused', label: 'Conversion-Focused', description: 'Soft, indirect product nudge.' },
  { value: 'authority_building', label: 'Authority-Building', description: 'Deep expertise with specifics.' },
  { value: 'viral_controversial', label: 'Viral / Controversial', description: 'Contrarian take, backed by reasoning.' },
];

const INTENT_OPTIONS: Array<PillOption<AdvancedIntent>> = [
  { value: 'educate', label: 'Educate', description: 'Lead with useful info.' },
  { value: 'soft_recommend', label: 'Soft Recommend', description: 'Personal preference, not a pitch.' },
  { value: 'hard_recommend', label: 'Hard Recommend', description: 'Direct, confident recommendation.' },
  { value: 'build_trust', label: 'Build Trust', description: 'Show credibility & nuance.' },
  { value: 'counter_argument', label: 'Counter Argument', description: 'Respectfully push back.' },
  { value: 'story_persuasion', label: 'Story Persuasion', description: 'Persuade via short anecdote.' },
];

const AUDIENCE_OPTIONS: Array<PillOption<AdvancedAudienceAwareness>> = [
  { value: 'unaware', label: 'Unaware', description: "Reader doesn't know the problem yet." },
  { value: 'problem_aware', label: 'Problem Aware', description: 'Knows the pain, not the fix.' },
  { value: 'solution_aware', label: 'Solution Aware', description: 'Comparing solution categories.' },
  { value: 'product_aware', label: 'Product Aware', description: 'Evaluating specific products.' },
];

const PERSONA_OPTIONS: Array<PillOption<AdvancedPersona>> = [
  { value: 'expert', label: 'Expert', description: 'Knowledgeable, plain-spoken.' },
  { value: 'real_user', label: 'Real User', description: 'First-hand customer experience.' },
  { value: 'founder', label: 'Founder', description: 'Builder, candid about wins/losses.' },
  { value: 'neutral_observer', label: 'Neutral Observer', description: 'No skin in the game.' },
];

const DEPTH_OPTIONS: Array<PillOption<AdvancedDepth>> = [
  { value: 'short', label: 'Short', description: '1–3 lines, single insight.' },
  { value: 'medium', label: 'Medium', description: '3–6 sentences, one clear thread.' },
  { value: 'deep', label: 'Deep', description: '200–400 words, multiple angles.' },
];

const FORMAT_OPTIONS: Array<PillOption<AdvancedReplyFormat>> = [
  { value: 'paragraph', label: 'Paragraph', description: 'Natural Reddit prose.' },
  { value: 'bullets', label: 'Bullets', description: '3–6 short bullets.' },
  { value: 'story_insight', label: 'Story + Insight', description: 'Story → takeaway.' },
  { value: 'step_by_step', label: 'Step by Step', description: 'Numbered walk-through.' },
];

const MENTION_OPTIONS: Array<PillOption<AdvancedMentionProduct>> = [
  { value: 'none', label: 'None', description: "Don't mention the brand." },
  { value: 'indirect', label: 'Indirect', description: '"A tool I tried" — no link.' },
  { value: 'direct', label: 'Direct', description: 'Name it once, naturally.' },
];

const CTA_OPTIONS: Array<PillOption<AdvancedCtaType>> = [
  { value: 'none', label: 'No CTA', description: 'End on the natural conclusion.' },
  { value: 'question', label: 'Question', description: 'Open-ended invite to share.' },
  { value: 'soft_suggestion', label: 'Soft Suggestion', description: '"Might be worth a look".' },
];

const ADVANCED_TONE_OPTIONS: Array<PillOption<AdvancedToneOverlay>> = [
  { value: 'casual', label: 'Casual', description: 'Like talking to a friend.' },
  { value: 'analytical', label: 'Analytical', description: 'Measured, show your work.' },
  { value: 'opinionated', label: 'Opinionated', description: 'Confident & direct.' },
];

const STRATEGY_PRESET_DEFAULTS: Record<
  AdvancedStrategyPreset,
  Partial<{
    intent: AdvancedIntent;
    audience_awareness: AdvancedAudienceAwareness;
    persona: AdvancedPersona;
    depth: AdvancedDepth;
    reply_format: AdvancedReplyFormat;
    mention_product: AdvancedMentionProduct;
    cta_type: AdvancedCtaType;
    advanced_tone: AdvancedToneOverlay;
  }>
> = {
  safe: { intent: 'educate', mention_product: 'none', cta_type: 'none', advanced_tone: 'analytical', depth: 'medium' },
  high_engagement: { intent: 'story_persuasion', persona: 'real_user', reply_format: 'story_insight', cta_type: 'question' },
  conversion_focused: { intent: 'soft_recommend', mention_product: 'indirect', cta_type: 'soft_suggestion', depth: 'medium' },
  authority_building: { intent: 'build_trust', persona: 'expert', depth: 'deep', reply_format: 'bullets', advanced_tone: 'analytical' },
  viral_controversial: { intent: 'counter_argument', persona: 'neutral_observer', advanced_tone: 'opinionated', cta_type: 'question' },
};

const recommendationCache = new Map<string, AIReplyRecommendResponse>();

const buildCacheKey = (
  postUrl: string | undefined,
  postTitle: string,
  postContent: string,
  targetCommentId: string | undefined
) => {
  const base = postUrl?.trim() || `${postTitle}::${postContent}`;
  return targetCommentId ? `${base}::${targetCommentId}` : base;
};

type AdvancedFieldKey =
  | 'strategy_preset'
  | 'intent'
  | 'audience_awareness'
  | 'persona'
  | 'depth'
  | 'reply_format'
  | 'mention_product'
  | 'cta_type'
  | 'advanced_tone';

// ---------------------------------------------------------------------------
// Section primitives
// ---------------------------------------------------------------------------

const Section: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
  emphasis?: boolean;
  style?: React.CSSProperties;
}> = ({ title, subtitle, extra, children, emphasis, style }) => (
  <div
    style={{
      marginBottom: 14,
      background: T.bg,
      border: `1px solid ${emphasis ? '#FFE4D6' : T.border}`,
      borderRadius: 10,
      overflow: 'hidden',
      ...style,
    }}
  >
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        padding: '10px 14px',
        borderBottom: `1px solid ${T.border}`,
        background: emphasis ? '#FFF7F2' : T.bgMuted,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Text strong style={{ fontSize: 12.5, color: T.text, letterSpacing: 0.1 }}>
          {title}
        </Text>
        {subtitle && (
          <Text style={{ fontSize: 12, color: T.textMuted, fontWeight: 400 }}>{subtitle}</Text>
        )}
      </div>
      {extra && <div>{extra}</div>}
    </div>
    <div style={{ padding: '14px' }}>{children}</div>
  </div>
);

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean; hint?: React.ReactNode }> = ({
  children,
  required,
  hint,
}) => (
  <div style={{ marginBottom: 8 }}>
    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text }}>
      {children}
      {required && <span style={{ color: T.brand, marginLeft: 4 }}>*</span>}
    </span>
    {hint && (
      <span style={{ fontSize: 12, color: T.textMuted, marginLeft: 6, fontWeight: 400 }}>
        {hint}
      </span>
    )}
  </div>
);

// ---------------------------------------------------------------------------
// Pill grid
// ---------------------------------------------------------------------------

function PillGrid<T_ extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: Array<PillOption<T_>>;
  value: T_ | undefined;
  onChange: (v: T_ | undefined) => void;
  disabled?: boolean;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <Tooltip key={opt.value} title={opt.description} mouseEnterDelay={0.4}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onChange(selected ? undefined : opt.value)}
              style={{
                cursor: disabled ? 'not-allowed' : 'pointer',
                fontSize: 12.5,
                fontWeight: selected ? 600 : 500,
                padding: '6px 12px',
                borderRadius: 999,
                border: `1px solid ${selected ? T.text : T.border}`,
                background: selected ? T.text : T.bg,
                color: selected ? '#fff' : T.text,
                lineHeight: 1.3,
                transition: 'all 0.12s ease',
                opacity: disabled ? 0.5 : 1,
                outline: 'none',
              }}
              onMouseEnter={(e) => {
                if (!disabled && !selected) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = T.borderStrong;
                  (e.currentTarget as HTMLButtonElement).style.background = T.bgMuted;
                }
              }}
              onMouseLeave={(e) => {
                if (!disabled && !selected) {
                  (e.currentTarget as HTMLButtonElement).style.borderColor = T.border;
                  (e.currentTarget as HTMLButtonElement).style.background = T.bg;
                }
              }}
            >
              {opt.label}
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
}

const toCommentContext = (comment: NestedComment): CommentContext => ({
  id: comment.id,
  body: comment.body,
  author: comment.author,
  score: comment.score,
  depth: comment.depth,
  is_submitter: comment.is_submitter,
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AdvancedReplyForm = forwardRef<AdvancedReplyFormHandle, AdvancedReplyFormProps>(
  (
    {
      postTitle,
      postContent,
      postUrl,
      subreddit,
      brandId,
      brandInfo,
      initialKeywords,
      commentTarget,
      guidelines,
      loading,
      currentStage,
      onCanGenerateChange,
      onStartGeneration,
    },
    ref
  ) => {
    const [form] = Form.useForm();

    const [userThought, setUserThought] = useState('');
    const [strategyPreset, setStrategyPreset] = useState<AdvancedStrategyPreset | undefined>();
    const [intent, setIntent] = useState<AdvancedIntent | undefined>();
    const [audienceAwareness, setAudienceAwareness] = useState<AdvancedAudienceAwareness | undefined>();
    const [persona, setPersona] = useState<AdvancedPersona | undefined>();
    const [depth, setDepth] = useState<AdvancedDepth | undefined>();
    const [replyFormat, setReplyFormat] = useState<AdvancedReplyFormat | undefined>();
    const [mentionProduct, setMentionProduct] = useState<AdvancedMentionProduct | undefined>();
    const [ctaType, setCtaType] = useState<AdvancedCtaType | undefined>();
    const [advancedTone, setAdvancedTone] = useState<AdvancedToneOverlay | undefined>();
    const [keywords, setKeywords] = useState<string[]>([]);

    const [recommending, setRecommending] = useState(false);
    const [recommendation, setRecommendation] = useState<AIReplyRecommendResponse | null>(null);
    const [recommendError, setRecommendError] = useState<string | null>(null);
    const touchedRef = useRef<Set<AdvancedFieldKey>>(new Set());

    const canGenerate = useMemo(() => {
      const hasThought = userThought.trim().length > 0;
      const hasMinCore = !!intent && !!persona && !!depth;
      return hasThought || hasMinCore;
    }, [userThought, intent, persona, depth]);

    useEffect(() => {
      onCanGenerateChange(canGenerate);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canGenerate]);

    useEffect(() => {
      if (initialKeywords && initialKeywords.length > 0) {
        setKeywords(initialKeywords);
      }
      if (brandInfo?.trim()) {
        form.setFieldsValue({ brand_info: brandInfo });
      }
      touchedRef.current = new Set();
      const cacheKey = buildCacheKey(postUrl, postTitle, postContent, commentTarget?.comment.id);
      const cached = recommendationCache.get(cacheKey);
      if (cached) {
        applyRecommendation(cached, { overwriteAll: true });
      } else {
        void runRecommend({ overwriteAll: true });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const markTouched = (key: AdvancedFieldKey) => {
      touchedRef.current.add(key);
    };

    const applyRecommendation = (
      r: AIReplyRecommendResponse,
      { overwriteAll = false }: { overwriteAll?: boolean } = {}
    ) => {
      const touched = touchedRef.current;
      const fill = (key: AdvancedFieldKey) => overwriteAll || !touched.has(key);
      if (fill('strategy_preset')) setStrategyPreset(r.strategy_preset);
      if (fill('intent')) setIntent(r.intent);
      if (fill('audience_awareness')) setAudienceAwareness(r.audience_awareness);
      if (fill('persona')) setPersona(r.persona);
      if (fill('depth')) setDepth(r.depth);
      if (fill('reply_format')) setReplyFormat(r.reply_format);
      if (fill('mention_product')) setMentionProduct(r.mention_product);
      if (fill('cta_type')) setCtaType(r.cta_type);
      if (fill('advanced_tone')) setAdvancedTone(r.advanced_tone);
      setRecommendation(r);
    };

    const runRecommend = async ({ overwriteAll = false }: { overwriteAll?: boolean } = {}) => {
      if (!postTitle && !postContent) return;
      setRecommending(true);
      setRecommendError(null);
      try {
        const r = await aiReplyService.recommendStrategy({
          post_title: postTitle,
          post_content: postContent,
          post_url: postUrl,
          subreddit,
          brand_id: brandId,
          brand_info: brandInfo,
          target_comment: commentTarget ? toCommentContext(commentTarget.comment) : undefined,
          parent_comments: commentTarget?.parentChain.map(toCommentContext),
          user_custom_intent: userThought.trim() || undefined,
        });
        const cacheKey = buildCacheKey(postUrl, postTitle, postContent, commentTarget?.comment.id);
        recommendationCache.set(cacheKey, r);
        applyRecommendation(r, { overwriteAll });
      } catch (err: any) {
        console.warn('[AdvancedReplyForm] auto-suggest failed:', err);
        setRecommendError(err?.message || 'Auto-suggest failed');
      } finally {
        setRecommending(false);
      }
    };

    const handleSelectPreset = (preset: AdvancedStrategyPreset | undefined) => {
      setStrategyPreset(preset);
      if (!preset) return;
      const defaults = STRATEGY_PRESET_DEFAULTS[preset];
      if (!intent && defaults.intent) setIntent(defaults.intent);
      if (!audienceAwareness && defaults.audience_awareness) setAudienceAwareness(defaults.audience_awareness);
      if (!persona && defaults.persona) setPersona(defaults.persona);
      if (!depth && defaults.depth) setDepth(defaults.depth);
      if (!replyFormat && defaults.reply_format) setReplyFormat(defaults.reply_format);
      if (!mentionProduct && defaults.mention_product) setMentionProduct(defaults.mention_product);
      if (!ctaType && defaults.cta_type) setCtaType(defaults.cta_type);
      if (!advancedTone && defaults.advanced_tone) setAdvancedTone(defaults.advanced_tone);
    };

    const handleGenerate = () => {
      if (!canGenerate) {
        message.warning('Add a thought in Section 0, or pick at least an Intent, Persona, and Depth.');
        return;
      }

      const formValues = form.getFieldsValue();
      const customWording = keywords.length > 0 ? keywords.join(', ') : undefined;

      const params: GenerateParams = {
        source: 'advanced',
        post_title: postTitle,
        post_content: postContent,
        post_url: postUrl,
        subreddit,
        brand_info: formValues.brand_info?.trim() || brandInfo || undefined,
        brand_id: brandId,
        custom_wording: customWording,
        tone_preference: advancedTone ?? 'neutral',
        force_brand_mention: !!formValues.force_brand_mention,
        target_comment: commentTarget ? toCommentContext(commentTarget.comment) : undefined,
        parent_comments: commentTarget?.parentChain.map(toCommentContext),
        capturedCommentId: commentTarget?.comment.id,
        user_custom_intent: userThought.trim() || undefined,
        strategy_preset: strategyPreset,
        intent,
        audience_awareness: audienceAwareness,
        persona,
        depth,
        reply_format: replyFormat,
        mention_product: mentionProduct,
        cta_type: ctaType,
        advanced_tone: advancedTone,
        is_advanced: true,
      };

      onStartGeneration(params);
    };

    useImperativeHandle(ref, () => ({
      generate: () => {
        void handleGenerate();
      },
    }));

    const isInitialRecommending = recommending && !recommendation && !recommendError;

    return (
      <Form form={form} layout="vertical" disabled={loading} requiredMark={false}>
        {/* SECTION 0 — Your thought */}
        <Section
          emphasis
          title={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <BulbOutlined style={{ color: T.brand, fontSize: 13 }} />
              Your thought
            </span>
          }
          subtitle={
            <Tag
              style={{
                margin: 0,
                fontSize: 10.5,
                padding: '0 6px',
                lineHeight: '18px',
                background: '#FFEDE0',
                color: T.brand,
                border: '1px solid #FFD4BC',
                fontWeight: 600,
              }}
            >
              Optional · Highest priority
            </Tag>
          }
        >
          <TextArea
            rows={3}
            value={userThought}
            onChange={(e) => setUserThought(e.target.value)}
            placeholder={
              'e.g. "I want to subtly suggest my service without sounding salesy" / "I disagree but want to sound respectful"'
            }
            disabled={loading}
            showCount
            maxLength={500}
            style={{ borderRadius: 6, fontSize: 13 }}
          />
          <Text style={{ fontSize: 12, display: 'block', marginTop: 8, color: T.textMuted }}>
            If filled, this becomes the highest-priority instruction. Selections below refine, not override it.
          </Text>
        </Section>

        {/* STRATEGY PRESET */}
        <Section
          title="Strategy preset"
          subtitle="auto-fills controls below"
          extra={
            <Tooltip title="Re-run AI auto-suggest. Overwrites all pills with fresh picks.">
              <Button
                type="text"
                size="small"
                icon={recommending ? <LoadingOutlined /> : <ThunderboltOutlined />}
                onClick={() => void runRecommend({ overwriteAll: true })}
                disabled={loading || recommending}
                style={{
                  padding: '0 6px',
                  height: 24,
                  color: T.brand,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {recommending ? 'Choosing…' : 'Re-suggest with AI'}
              </Button>
            </Tooltip>
          }
        >
          {!isInitialRecommending && (
            <PillGrid
              options={STRATEGY_PRESETS}
              value={strategyPreset}
              onChange={(v) => {
                markTouched('strategy_preset');
                handleSelectPreset(v);
              }}
              disabled={loading || recommending}
            />
          )}
          {recommending && !recommendation && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LoadingOutlined style={{ color: T.textMuted, fontSize: 12 }} />
              <Text style={{ fontSize: 12, color: T.textSecondary }}>
                AI is reading the post{subreddit ? `, r/${subreddit} rules` : ''}
                {brandId ? ', and your brand context' : ''} to pick the best strategy…
              </Text>
            </div>
          )}
          {!recommending && recommendation?.rationale && (
            <div style={{ marginTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  padding: '8px 10px',
                  background: T.bgMuted,
                  border: `1px solid ${T.border}`,
                  borderRadius: 6,
                }}
              >
                <BulbOutlined style={{ color: T.brand, fontSize: 12, marginTop: 2, flexShrink: 0 }} />
                <Text style={{ fontSize: 12, color: T.textSecondary, lineHeight: 1.5 }}>
                  {recommendation.rationale}
                </Text>
              </div>
              {(recommendation.subreddit_guidelines_applied || recommendation.brand_context_used) && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {recommendation.subreddit_guidelines_applied && (
                    <Tag
                      icon={<SafetyCertificateOutlined />}
                      style={{
                        margin: 0,
                        fontSize: 11,
                        padding: '0 6px',
                        lineHeight: '20px',
                        background: '#ECFDF5',
                        color: '#047857',
                        border: '1px solid #A7F3D0',
                      }}
                    >
                      Used r/{subreddit} rules
                    </Tag>
                  )}
                  {recommendation.brand_context_used && (
                    <Tag
                      style={{
                        margin: 0,
                        fontSize: 11,
                        padding: '0 6px',
                        lineHeight: '20px',
                        background: '#EFF6FF',
                        color: '#1D4ED8',
                        border: '1px solid #BFDBFE',
                      }}
                    >
                      Used brand knowledge
                    </Tag>
                  )}
                </div>
              )}
            </div>
          )}
          {!recommending && recommendError && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 10, borderRadius: 6 }}
              message="Couldn't auto-suggest a strategy"
              description={
                <Space>
                  <Text style={{ fontSize: 12 }}>You can still pick controls manually below.</Text>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => void runRecommend({ overwriteAll: true })}
                    style={{ padding: 0, color: T.brand, fontWeight: 600 }}
                  >
                    Retry
                  </Button>
                </Space>
              }
            />
          )}
        </Section>

        {/* SECTION 1 — STRATEGY CORE */}
        {!isInitialRecommending && (
          <Section title="Strategy core">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <FieldLabel required>Intent</FieldLabel>
                <PillGrid
                  options={INTENT_OPTIONS}
                  value={intent}
                  onChange={(v) => {
                    markTouched('intent');
                    setIntent(v);
                  }}
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel>Audience awareness</FieldLabel>
                <PillGrid
                  options={AUDIENCE_OPTIONS}
                  value={audienceAwareness}
                  onChange={(v) => {
                    markTouched('audience_awareness');
                    setAudienceAwareness(v);
                  }}
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel required>Persona</FieldLabel>
                <PillGrid
                  options={PERSONA_OPTIONS}
                  value={persona}
                  onChange={(v) => {
                    markTouched('persona');
                    setPersona(v);
                  }}
                  disabled={loading}
                />
              </div>
            </Space>
          </Section>
        )}

        {/* SECTION 2 — STRUCTURE */}
        {!isInitialRecommending && (
          <Section title="Structure">
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
              <div>
                <FieldLabel required>Depth</FieldLabel>
                <PillGrid
                  options={DEPTH_OPTIONS}
                  value={depth}
                  onChange={(v) => {
                    markTouched('depth');
                    setDepth(v);
                  }}
                  disabled={loading}
                />
              </div>
              <div>
                <FieldLabel>Format</FieldLabel>
                <PillGrid
                  options={FORMAT_OPTIONS}
                  value={replyFormat}
                  onChange={(v) => {
                    markTouched('reply_format');
                    setReplyFormat(v);
                  }}
                  disabled={loading}
                />
              </div>
            </Space>
          </Section>
        )}

        {/* SECTION 3 — CONVERSION (collapsible) */}
        {!isInitialRecommending && (
          <Collapse
            size="small"
            style={{
              marginBottom: 14,
              background: T.bg,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
            }}
            items={[
              {
                key: 'conversion',
                label: (
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text }}>
                    Conversion
                    <span style={{ marginLeft: 6, fontSize: 12, color: T.textMuted, fontWeight: 400 }}>
                      optional
                    </span>
                  </span>
                ),
                children: (
                  <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <div>
                      <FieldLabel>Mention product</FieldLabel>
                      <PillGrid
                        options={MENTION_OPTIONS}
                        value={mentionProduct}
                        onChange={(v) => {
                          markTouched('mention_product');
                          setMentionProduct(v);
                        }}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <FieldLabel>Call to action</FieldLabel>
                      <PillGrid
                        options={CTA_OPTIONS}
                        value={ctaType}
                        onChange={(v) => {
                          markTouched('cta_type');
                          setCtaType(v);
                        }}
                        disabled={loading}
                      />
                    </div>
                    <div>
                      <FieldLabel>Tone overlay</FieldLabel>
                      <PillGrid
                        options={ADVANCED_TONE_OPTIONS}
                        value={advancedTone}
                        onChange={(v) => {
                          markTouched('advanced_tone');
                          setAdvancedTone(v);
                        }}
                        disabled={loading}
                      />
                    </div>
                  </Space>
                ),
              },
            ]}
          />
        )}

        {/* BRAND & KEYWORDS */}
        {!isInitialRecommending && (
          <Section title="Brand & keywords">
            <Form.Item
              label={<span style={{ fontSize: 12.5, fontWeight: 600, color: T.text }}>Brand or product to embed</span>}
              name="brand_info"
              tooltip="Mentioned naturally if your Conversion settings allow it."
              style={{ marginBottom: 14 }}
            >
              <Input
                placeholder="e.g., MyApp, TechSolutions"
                disabled={loading}
                style={{ borderRadius: 6 }}
              />
            </Form.Item>
            {guidelines?.restricts_promotion && (
              <Form.Item
                label={
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12.5, fontWeight: 600, color: T.text }}>Force brand mention</span>
                    <span style={{ fontSize: 12, color: T.warning, fontWeight: 400 }}>
                      r/{subreddit} restricts self-promotion. Forcing may cause removal.
                    </span>
                  </span>
                }
                name="force_brand_mention"
                valuePropName="checked"
                initialValue={false}
                tooltip={`r/${subreddit} restricts self-promotion. Turn this on to force the mention anyway.`}
                style={{ marginBottom: 14 }}
              >
                <Switch disabled={loading} size="small" />
              </Form.Item>
            )}
            <div>
              <FieldLabel hint="Press Enter or comma to add">Keywords to include</FieldLabel>
              <KeywordsInput
                value={keywords}
                onChange={setKeywords}
                disabled={loading}
                tagColor="default"
              />
            </div>
          </Section>
        )}

        {/* Loading progress */}
        {loading && (
          <div
            style={{
              marginBottom: 14,
              padding: '14px 14px 12px',
              background: T.bgMuted,
              border: `1px solid ${T.border}`,
              borderRadius: 10,
            }}
          >
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Steps
                current={currentStage}
                size="small"
                items={[
                  { title: 'Planning', icon: currentStage === 0 ? <LoadingOutlined /> : undefined },
                  { title: 'Drafting', icon: currentStage === 1 ? <LoadingOutlined /> : undefined },
                  { title: 'Humanizing', icon: currentStage === 2 ? <LoadingOutlined /> : undefined },
                  { title: 'Compiling 3 variants', icon: currentStage === 3 ? <LoadingOutlined /> : undefined },
                ]}
              />
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                }}
              >
                <LoadingOutlined style={{ color: T.brand, fontSize: 12 }} />
                <Text style={{ color: T.textSecondary, fontSize: 13 }}>
                  Generating 3 variants (Safe / Engaging / Persuasive)…
                </Text>
              </div>
            </Space>
          </div>
        )}
      </Form>
    );
  }
);

AdvancedReplyForm.displayName = 'AdvancedReplyForm';
export default AdvancedReplyForm;
